<script setup lang="ts">
import { watch, computed } from "vue";
import { required } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import CheckIntervalType = App.Enums.CheckIntervalType;
import CheckIntervalSpecificationDTO = App.Data.Alerts.CheckIntervalSpecificationDTO;

const { t } = useI18n();

const props = defineProps<{
	intervalType: CheckIntervalType;
	intervalSpecification: CheckIntervalSpecificationDTO | null;
}>();

const intervalSpecification = computed({
	get() {
		return props.intervalSpecification;
	},
	set() {
		// void because of updateIntervalSpecification() that's being called on @update:modelValue
	},
});

const intervalType = computed({
	get() {
		return props.intervalType;
	},
	set(newValue) {
		emit("update:intervalType", newValue);
	},
});

const emit = defineEmits([
	"update:intervalType",
	"update:intervalSpecification",
]);

const DEFAULT_HOUR = 9;
const DEFAULT_MINUTE = 0;

const intervalTypes = [
	{
		value: "every_minute",
		displayName: "Every minute",
		icon: "mdi-clock-fast",
	},
	{ value: "hourly", displayName: "Hourly", icon: "mdi-clock-time-one" },
	{ value: "daily", displayName: "Daily", icon: "mdi-calendar-today" },
	{ value: "weekly", displayName: "Weekly", icon: "mdi-calendar-week" },
	{ value: "monthly", displayName: "Monthly", icon: "mdi-calendar-month" },
];

const daysOfWeek = [
	{ value: 0, displayName: "Sunday" },
	{ value: 1, displayName: "Monday" },
	{ value: 2, displayName: "Tuesday" },
	{ value: 3, displayName: "Wednesday" },
	{ value: 4, displayName: "Thursday" },
	{ value: 5, displayName: "Friday" },
	{ value: 6, displayName: "Saturday" },
];

const daysOfMonth = computed(() => {
	const days = [
		{ value: -1, displayName: "Last day", icon: "mdi-calendar-end" },
	];

	for (let i = 1; i <= 31; i++) {
		days.push({
			value: i,
			displayName: i.toString(),
			icon: i <= 9 ? `mdi-numeric-${i}-box` : "mdi-calendar-cursor",
		});
	}
	return days;
});

watch(
	() => intervalType.value,
	(interval_type) => {
		if (interval_type === "every_minute") {
			emit("update:intervalSpecification", null);
		}
		if (interval_type === "hourly") {
			emit("update:intervalSpecification", {
				minute: DEFAULT_MINUTE,
				...intervalSpecification.value,
			});
		}
		if (interval_type === "daily") {
			emit("update:intervalSpecification", {
				hour: DEFAULT_HOUR,
				minute: DEFAULT_MINUTE,
				...intervalSpecification.value,
			});
		}
		if (interval_type === "weekly") {
			emit("update:intervalSpecification", {
				day_of_week: 1,
				hour: DEFAULT_HOUR,
				minute: DEFAULT_MINUTE,
				...intervalSpecification.value,
			});
		}
		if (interval_type === "monthly") {
			emit("update:intervalSpecification", {
				day_of_month: -1, // default it to last day of month
				hour: DEFAULT_HOUR,
				minute: DEFAULT_MINUTE,
				...intervalSpecification.value,
			});
		}
	}
);

const updateIntervalSpecification = (value, granularity) => {
	intervalSpecification.value[granularity] = value;
	emit("update:intervalSpecification", intervalSpecification.value);
};

const formatNumber = (num) => {
	return num < 10 ? `0${num}` : `${num}`;
};

// Function to get appropriate label based on interval type
const getMinuteLabel = () => {
	if (intervalType.value === "hourly") {
		return "At Minute";
	}
	return "Minute";
};
</script>

<template>
	<div class="d-flex flex-column ga-2">
		<v-select
			v-model="intervalType"
			:items="intervalTypes"
			item-title="displayName"
			item-value="value"
			label="Frequency"
			hide-details="auto"
			:rules="[required(t)]"
		>
		</v-select>
		<v-expand-transition>
			<div v-if="intervalType !== 'every_minute'" class="timing-grid">
				<!-- Monthly day selection -->
				<v-select
					v-if="
						intervalType === 'monthly' &&
						intervalSpecification?.hasOwnProperty('day_of_month')
					"
					v-model="intervalSpecification.day_of_month"
					@update:modelValue="
						(newValue) => updateIntervalSpecification(newValue, 'day_of_month')
					"
					:items="daysOfMonth"
					item-title="displayName"
					item-value="value"
					label="Day of Month"
					hide-details="auto"
					:rules="[required(t)]"
				/>

				<!-- Weekly day selection -->
				<v-select
					v-if="
						intervalType === 'weekly' &&
						intervalSpecification?.hasOwnProperty('day_of_week')
					"
					v-model="intervalSpecification.day_of_week"
					@update:modelValue="
						(newValue) => updateIntervalSpecification(newValue, 'day_of_week')
					"
					:items="daysOfWeek"
					item-title="displayName"
					item-value="value"
					label="Day of Week"
					hide-details="auto"
					:rules="[required(t)]"
				/>

				<!-- Hour selection -->
				<v-select
					v-if="
						(intervalType === 'monthly' ||
							intervalType === 'weekly' ||
							intervalType === 'daily') &&
						intervalSpecification?.hasOwnProperty('hour')
					"
					v-model="intervalSpecification.hour"
					@update:modelValue="
						(newValue) => updateIntervalSpecification(newValue, 'hour')
					"
					:items="
						Array.from({ length: 24 }, (_, i) => ({
							value: i,
							title: formatNumber(i),
						}))
					"
					item-title="title"
					item-value="value"
					label="At Hour"
					hide-details="auto"
					:rules="[required(t)]"
				/>

				<!-- Minute selection -->
				<v-select
					v-if="intervalSpecification?.hasOwnProperty('minute')"
					v-model="intervalSpecification.minute"
					@update:modelValue="
						(newValue) => updateIntervalSpecification(newValue, 'minute')
					"
					:items="
						Array.from({ length: 60 }, (_, i) => ({
							value: i,
							title: formatNumber(i),
						}))
					"
					item-title="title"
					item-value="value"
					:label="getMinuteLabel()"
					hide-details="auto"
					:rules="[required(t)]"
				/>
			</div>
		</v-expand-transition>
	</div>
</template>

<style scoped lang="scss">
.timing-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
	gap: 0.5rem;
}
</style>
